/* KORA CUSTOM START - hero slideshow */

const selector = '[data-kora-hero-slideshow]';

class KoraHeroSlideshow extends HTMLElement {
  #scroller;
  #slides;
  #dots;
  #previousButton;
  #nextButton;
  #observer;
  #autoplayTimer;
  #isPaused = false;

  connectedCallback() {
    this.#scroller = this.querySelector('[data-kora-hero-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-kora-hero-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-kora-hero-dot]'));
    this.#previousButton = this.querySelector('[data-kora-hero-previous]');
    this.#nextButton = this.querySelector('[data-kora-hero-next]');

    if (!this.#scroller || this.#slides.length === 0) return;

    this.#previousButton?.addEventListener('click', this.#goPrevious);
    this.#nextButton?.addEventListener('click', this.#goNext);
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index));
    });

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#observeSlides();
    this.#startAutoplay();
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#previousButton?.removeEventListener('click', this.#goPrevious);
    this.#nextButton?.removeEventListener('click', this.#goNext);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.#observer?.disconnect();
    window.clearInterval(this.#autoplayTimer);
  }

  #observeSlides() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible?.target) return;

        const index = this.#slides.indexOf(visible.target);
        if (index >= 0) this.#setActive(index);
      },
      {
        root: this.#scroller,
        threshold: [0.55, 0.7, 0.9],
      }
    );

    this.#slides.forEach((slide) => this.#observer?.observe(slide));
  }

  #startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.#slides.length <= 1) return;

    const interval = Number(this.dataset.autoplayInterval || 6) * 1000;
    this.#autoplayTimer = window.setInterval(() => {
      if (this.#isPaused) return;
      this.#goTo(this.#currentIndex() + 1);
    }, interval);
  }

  #currentIndex() {
    const active = this.#dots.findIndex((dot) => dot.getAttribute('aria-current') === 'true');
    return active >= 0 ? active : 0;
  }

  #goPrevious = () => {
    this.#goTo(this.#currentIndex() - 1);
  };

  #goNext = () => {
    this.#goTo(this.#currentIndex() + 1);
  };

  #goTo(index) {
    if (this.#slides.length === 0) return;

    const nextIndex = (index + this.#slides.length) % this.#slides.length;
    this.#slides[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
    this.#setActive(nextIndex);
  }

  #setActive(index) {
    this.#slides.forEach((slide, slideIndex) => {
      slide.toggleAttribute('data-active', slideIndex === index);
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    this.#dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  }

  #pause = () => {
    this.#isPaused = true;
  };

  #resume = () => {
    this.#isPaused = false;
  };
}

if (!customElements.get('kora-hero-slideshow')) {
  customElements.define('kora-hero-slideshow', KoraHeroSlideshow);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(selector).forEach((element) => {
    if (!customElements.get('kora-hero-slideshow')) return;
    if (element instanceof KoraHeroSlideshow) return;
    customElements.upgrade(element);
  });
});

/* KORA CUSTOM END - hero slideshow */
